// Dashboard/SalesAnalytics.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Dexie from 'dexie';
import {
  FiBarChart2, FiTrendingUp, FiDollarSign, FiShoppingBag,
  FiRefreshCw, FiDownload, FiSearch, FiChevronLeft, FiChevronRight,
  FiChevronsLeft, FiChevronsRight, FiPieChart, FiUsers, FiPackage,
  FiAward, FiCpu, FiMessageCircle, FiZap, FiActivity, FiTarget,
  FiWifi, FiWifiOff, FiDatabase, FiClock, FiXCircle, FiMenu, FiX,
  FiPercent, FiLayers, FiBarChart,
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, ZAxis, Legend,
} from 'recharts';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';

// -------------------- BASE DE DONNÉES & UTILITAIRES --------------------
const db = new Dexie('SalesAnalyticsCache');
db.version(1).stores({
  articles: 'id, company_name, description, unit_price, cacheId',
  cacheInfo: 'key',
  requestLog: '++id, timestamp, endpoint',
});

class RateLimiter {
  constructor(maxRequests = 5, timeWindow = 60000) {
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindow;
    this.requests = [];
    this.requestQueue = [];
    this.isProcessing = false;
  }

  async acquire() {
    return new Promise((resolve) => {
      this.requestQueue.push(resolve);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      const now = Date.now();
      this.requests = this.requests.filter(time => now - time < this.timeWindow);

      if (this.requests.length < this.maxRequests) {
        const resolve = this.requestQueue.shift();
        this.requests.push(now);
        resolve();
      } else {
        const oldestRequest = this.requests[0];
        const waitTime = this.timeWindow - (now - oldestRequest);
        await new Promise(resolve => setTimeout(resolve, waitTime + 100));
      }
    }

    this.isProcessing = false;
  }
}

const fetchWithRetry = async (url, options = {}, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
};

const rateLimiter = new RateLimiter(3, 60000);

const logRequest = async (endpoint) => {
  try {
    await db.requestLog.add({ endpoint, timestamp: new Date().toISOString() });
    const count = await db.requestLog.count();
    if (count > 100) {
      const oldest = await db.requestLog.orderBy('id').first();
      if (oldest) await db.requestLog.delete(oldest.id);
    }
  } catch (error) {
    console.error('Erreur logging:', error);
  }
};

const isCacheValid = async () => {
  try {
    const cacheInfo = await db.cacheInfo.get('sales_analytics');
    if (!cacheInfo) return false;
    const now = new Date();
    const cacheDate = new Date(cacheInfo.timestamp);
    const hoursDiff = (now - cacheDate) / (1000 * 60 * 60);
    return hoursDiff < 24;
  } catch (error) {
    console.error('Erreur cache:', error);
    return false;
  }
};

const saveToCache = async (articles) => {
  try {
    await db.articles.clear();
    const articlesWithId = articles.map((article, index) => ({ ...article, cacheId: index + 1 }));
    if (articlesWithId.length > 0) {
      const batchSize = 50;
      for (let i = 0; i < articlesWithId.length; i += batchSize) {
        const batch = articlesWithId.slice(i, i + batchSize);
        await db.articles.bulkAdd(batch);
      }
    }
    await db.cacheInfo.put({ key: 'sales_analytics', timestamp: new Date().toISOString() });
  } catch (error) {
    console.error('Erreur sauvegarde cache:', error);
  }
};

const loadFromCache = async () => {
  try {
    const articles = await db.articles.toArray();
    return articles.map(({ cacheId, ...rest }) => rest);
  } catch (error) {
    console.error('Erreur chargement cache:', error);
    return [];
  }
};

const clearCache = async () => {
  try {
    await db.articles.clear();
    await db.cacheInfo.clear();
    console.log('Cache vidé');
  } catch (error) {
    console.error('Erreur vidage cache:', error);
  }
};

const MISTRAL_API_KEY = process.env.REACT_APP_MISTRAL_API_KEY || '';
const MISTRAL_API_URL = 'https://api.mistral.ai/v1/chat/completions';

// -------------------- COMPOSANTS RÉUTILISABLES --------------------
const Badge = ({ children, color = 'blue' }) => {
  const colors = {
    red: 'bg-red-500/10 border-red-500/20 text-red-400',
    green: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${colors[color] || colors.blue}`}>
      {children}
    </span>
  );
};

// Carte article mobile (inchangée)
const ArticleCardMobile = ({ article, onAnalyze, formatCurrency }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.div layout className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 mb-3">
      <div className="flex justify-between items-start cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-sm truncate">{article.description}</h3>
          <p className="text-slate-400 text-xs">{article.company_name}</p>
        </div>
        <div className="text-emerald-400 font-bold text-sm ml-2">{formatCurrency(article.unit_price)}</div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-3 mt-3 border-t border-white/10 space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-slate-400">ID</span><span>#{article.id}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Adresse</span><span>{article.address || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Total</span><span className="text-emerald-400">{formatCurrency(article.total)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Date</span><span>{article.created_at ? new Date(article.created_at).toLocaleDateString('fr-FR') : '—'}</span></div>
              <button
                onClick={() => onAnalyze(article)}
                className="w-full mt-2 py-2 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs flex items-center justify-center gap-1 hover:bg-purple-500/20"
              >
                <FiZap size={14} /> Analyser IA
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Menu mobile (inchangé)
const MobileMenu = ({ isOpen, onClose, onRefresh, onClearCache, onExport, isRefreshing }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
        <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25 }} className="fixed right-0 top-0 h-full w-72 bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border-l border-blue-400/20 shadow-2xl z-50 p-6">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-white font-bold text-lg">Menu</h3>
            <button onClick={onClose} className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white"><FiX size={18} /></button>
          </div>
          <div className="space-y-4">
            <button onClick={onRefresh} disabled={isRefreshing} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 disabled:opacity-50">
              <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} /> {isRefreshing ? 'Mise à jour...' : 'Actualiser'}
            </button>
            <button onClick={onClearCache} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400">
              <FiDatabase /> Vider le cache
            </button>
            <button onClick={onExport} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-blue-500/10 border border-blue-400/20 text-blue-400">
              <FiDownload /> Exporter CSV
            </button>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

// Donut personnalisé (inchangé)
const DonutChart = ({ data, size = 200 }) => {
  const radius = size / 2;
  let currentAngle = -90;
  const total = data.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) return <div className="flex items-center justify-center h-48 text-slate-400">Aucune donnée</div>;

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {data.map((item, idx) => {
          const angle = (item.value / total) * 360;
          if (angle === 0) return null;
          const startAngle = currentAngle;
          const endAngle = currentAngle + angle;
          currentAngle = endAngle;
          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;
          const x1 = radius + radius * Math.cos(startRad);
          const y1 = radius + radius * Math.sin(startRad);
          const x2 = radius + radius * Math.cos(endRad);
          const y2 = radius + radius * Math.sin(endRad);
          const largeArc = angle > 180 ? 1 : 0;
          return (
            <path
              key={idx}
              d={`M ${radius} ${radius} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`}
              fill={item.color}
              stroke="rgba(15,25,55,0.6)"
              strokeWidth="2"
            />
          );
        })}
        <circle cx={radius} cy={radius} r={radius * 0.6} fill="rgba(15,25,55,0.8)" />
        <text x={radius} y={radius} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="20" fontWeight="bold">
          {total}
        </text>
        <text x={radius} y={radius + 22} textAnchor="middle" dominantBaseline="middle" fill="rgba(170,200,255,0.6)" fontSize="10">
          Articles
        </text>
      </svg>
      <div className="flex flex-wrap gap-3 mt-4 justify-center">
        {data.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full" style={{ background: item.color }}></div>
            <span className="text-slate-300">{item.name}</span>
            <span className="text-white font-semibold">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Barre de progression (inchangée)
const ProgressBar = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-300">{label}</span>
        <span className="text-slate-400">{value} articles ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{ background: `linear-gradient(90deg, ${color}, ${color}cc)` }}
        />
      </div>
    </div>
  );
};

// -------------------- COMPOSANT PRINCIPAL --------------------
const SalesAnalytics = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cacheDate, setCacheDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const lastFetchRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Détection mobile / connexion
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const debouncedFetch = useCallback((callback, delay = 1000) => {
    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(callback, delay);
  }, []);

  // Récupération des données (identique, intégrée)
  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      if (!forceRefresh && lastFetchRef.current && Date.now() - lastFetchRef.current < 10000) {
        const cached = await loadFromCache();
        if (cached.length) {
          setData(cached);
          return;
        }
      }
      setLoading(true);
      setError(null);
      if (!forceRefresh) {
        const cacheValid = await isCacheValid();
        if (cacheValid) {
          const cached = await loadFromCache();
          if (cached.length) {
            setData(cached);
            const cacheInfo = await db.cacheInfo.get('sales_analytics');
            if (cacheInfo) setCacheDate(cacheInfo.timestamp);
            setSuccessMessage(`📦 ${cached.length} articles chargés depuis le cache`);
            setTimeout(() => setSuccessMessage(null), 3000);
            if (isOnline) debouncedFetch(() => refreshInBackground(), 15000);
            setLoading(false);
            return;
          }
        }
      }
      await rateLimiter.acquire();
      await logRequest('articles.php');
      const response = await fetchWithRetry(`${API_CONFIG.apiUrl}articles.php`);
      const result = await response.json();
      if (result.success) {
        setData(result.data);
        await saveToCache(result.data);
        lastFetchRef.current = Date.now();
        const cacheInfo = await db.cacheInfo.get('sales_analytics');
        if (cacheInfo) setCacheDate(cacheInfo.timestamp);
        setSuccessMessage(`✅ ${result.data.length} articles chargés`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(result.message);
      }
    } catch (err) {
      if (err.message?.includes('429') || err.status === 429) {
        setError('Trop de requêtes. Patientez...');
        setTimeout(() => fetchData(false), 30000);
      } else {
        const cached = await loadFromCache();
        if (cached.length) {
          setData(cached);
          setError(null);
        } else {
          setError('Impossible de charger les données.');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isOnline, debouncedFetch]);

  const refreshInBackground = async () => {
    try {
      await rateLimiter.acquire();
      const response = await fetchWithRetry(`${API_CONFIG.apiUrl}articles.php`);
      const result = await response.json();
      if (result.success) {
        setData(result.data);
        await saveToCache(result.data);
        lastFetchRef.current = Date.now();
      }
    } catch (err) { /* silent */ }
  };

  const manualRefresh = async () => {
    if (!isOnline) {
      setError('Pas de connexion internet.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    setIsRefreshing(true);
    try { await fetchData(true); } catch { setError('Échec du rafraîchissement'); }
    finally { setIsRefreshing(false); }
  };

  const clearCacheAndReload = async () => {
    await clearCache();
    lastFetchRef.current = null;
    await fetchData(true);
  };

  useEffect(() => {
    fetchData();
    return () => { if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current); };
  }, [fetchData]);

  // -------------------- STATISTIQUES ÉCONOMIQUES AVANCÉES --------------------
  const stats = useMemo(() => {
    const articles = data || [];
    if (articles.length === 0) {
      return {
        totalArticles: 0, totalValue: 0, averagePrice: 0, minPrice: 0, maxPrice: 0,
        medianPrice: 0, stdDevPrice: 0, coefficientVariation: 0,
        q1: 0, q3: 0, iqr: 0,
        topCompanies: [], topProducts: [],
        priceRanges: [],
        companyAnalysis: [], hhi: 0,
        productComparison: [], scatterData: [],
      };
    }

    const prices = articles.map(a => a.unit_price || 0).sort((a, b) => a - b);
    const totalArticles = prices.length;
    const totalValue = prices.reduce((s, p) => s + p, 0);
    const averagePrice = totalValue / totalArticles;
    const minPrice = prices[0];
    const maxPrice = prices[totalArticles - 1];

    // Médiane, quartiles
    const median = totalArticles % 2 === 0
      ? (prices[totalArticles / 2 - 1] + prices[totalArticles / 2]) / 2
      : prices[Math.floor(totalArticles / 2)];
    const q1 = prices[Math.floor(totalArticles * 0.25)];
    const q3 = prices[Math.floor(totalArticles * 0.75)];
    const iqr = q3 - q1;

    const variance = prices.reduce((s, p) => s + Math.pow(p - averagePrice, 2), 0) / totalArticles;
    const stdDev = Math.sqrt(variance);
    const cv = (averagePrice !== 0) ? (stdDev / averagePrice) * 100 : 0;

    // Analyse par entreprise
    const companyMap = new Map();
    articles.forEach(item => {
      const name = item.company_name;
      if (!companyMap.has(name)) {
        companyMap.set(name, { name, totalValue: 0, articleCount: 0, prices: [] });
      }
      const entry = companyMap.get(name);
      entry.totalValue += (item.unit_price || 0);
      entry.articleCount += 1;
      entry.prices.push(item.unit_price || 0);
    });
    const companyAnalysis = Array.from(companyMap.values()).map(c => ({
      ...c,
      avgPrice: c.articleCount > 0 ? c.totalValue / c.articleCount : 0,
      minPrice: Math.min(...c.prices),
      maxPrice: Math.max(...c.prices),
      shareValue: (c.totalValue / totalValue) * 100,
      shareVolume: (c.articleCount / totalArticles) * 100,
    })).sort((a, b) => b.totalValue - a.totalValue);

    const hhi = companyAnalysis.reduce((sum, c) => sum + Math.pow(c.shareValue, 2), 0);

    const topCompanies = companyAnalysis.slice(0, 5).map(c => ({ name: c.name, value: c.totalValue }));

    const topProducts = [...articles]
      .sort((a, b) => (b.unit_price || 0) - (a.unit_price || 0))
      .slice(0, 5);

    // Comparaison des prix par article (groupé par description)
    const productMap = new Map();
    articles.forEach(item => {
      const desc = item.description;
      if (!productMap.has(desc)) {
        productMap.set(desc, { description: desc, prices: [], companies: new Set() });
      }
      const entry = productMap.get(desc);
      entry.prices.push(item.unit_price || 0);
      entry.companies.add(item.company_name);
    });
    const productComparison = Array.from(productMap.values()).map(p => ({
      description: p.description,
      min: Math.min(...p.prices),
      max: Math.max(...p.prices),
      avg: p.prices.reduce((a, b) => a + b, 0) / p.prices.length,
      count: p.prices.length,
      companies: p.companies.size,
    })).sort((a, b) => b.count - a.count);

    // Données pour scatter plot
    const scatterData = companyAnalysis.map(c => ({
      name: c.name,
      avgPrice: c.avgPrice,
      articleCount: c.articleCount,
      totalValue: c.totalValue,
    }));

    const priceRanges = [
      { label: '< 1 000 FC', min: 0, max: 1000, count: 0, color: '#34d399' },
      { label: '1 000 - 5 000 FC', min: 1000, max: 5000, count: 0, color: '#60a5fa' },
      { label: '5 000 - 10 000 FC', min: 5000, max: 10000, count: 0, color: '#fbbf24' },
      { label: '10 000 - 50 000 FC', min: 10000, max: 50000, count: 0, color: '#f97316' },
      { label: '> 50 000 FC', min: 50000, max: Infinity, count: 0, color: '#ef4444' },
    ];
    articles.forEach(item => {
      const price = item.unit_price || 0;
      const range = priceRanges.find(r => price >= r.min && price < r.max);
      if (range) range.count++;
    });

    return {
      totalArticles, totalValue, averagePrice, minPrice, maxPrice,
      medianPrice: median, stdDevPrice: stdDev, coefficientVariation: cv,
      q1, q3, iqr,
      topCompanies, topProducts,
      priceRanges: priceRanges.filter(r => r.count > 0),
      companyAnalysis,
      hhi,
      productComparison,
      scatterData,
    };
  }, [data]);

  const donutData = stats.priceRanges.map(r => ({ name: r.label, value: r.count, color: r.color }));
  const barData = stats.topCompanies.map(c => ({ name: c.name.length > 15 ? c.name.substring(0, 15) + '...' : c.name, value: c.value }));

  // -------------------- IA MISTRAL --------------------
  const callMistralAI = async (query, context = null) => {
    if (!MISTRAL_API_KEY) return "⚠️ Analyse IA non disponible. Configurez la clé API.";
    setIsAiLoading(true);
    try {
      const statsSummary = {
        totalArticles: stats.totalArticles,
        averagePrice: formatCurrency(stats.averagePrice),
        medianPrice: formatCurrency(stats.medianPrice),
        stdDev: formatCurrency(stats.stdDevPrice),
        cv: stats.coefficientVariation.toFixed(1) + '%',
        hhi: stats.hhi.toFixed(0),
        companyCount: stats.companyAnalysis.length,
      };
      const prompt = context
        ? `Analyse: ${JSON.stringify(context)}\nQuestion: ${query}\nRéponds en français.`
        : `Voici les statistiques du marché : ${JSON.stringify(statsSummary)}\nQuestion: ${query}\nRéponds en français avec des recommandations économiques.`;
      const response = await fetchWithRetry(MISTRAL_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${MISTRAL_API_KEY}` },
        body: JSON.stringify({
          model: 'mistral-tiny',
          messages: [
            { role: 'system', content: 'Tu es un économiste expert en analyse de marché et de prix. Fournis des analyses précises, mentionne la concentration (HHI), la dispersion (écart-type), et donne des recommandations stratégiques.' },
            { role: 'user', content: prompt },
          ],
          temperature: 0.7,
          max_tokens: 500,
        }),
      });
      const result = await response.json();
      return result.choices[0].message.content;
    } catch (err) {
      return "Désolé, l'analyse IA n'est pas disponible.";
    } finally {
      setIsAiLoading(false);
    }
  };

  const askAI = async () => {
    if (!aiQuery.trim()) return;
    const response = await callMistralAI(aiQuery);
    setAiAnalysis(response);
    setSelectedProduct(null);
    setShowAiModal(true);
  };

  const analyzeProduct = async (product) => {
    const query = `Analyse ce produit: "${product.description}" au prix de ${product.unit_price} FC.`;
    const response = await callMistralAI(query, product);
    setAiAnalysis(response);
    setSelectedProduct(product);
    setShowAiModal(true);
  };

  const getGeneralAnalysis = async () => {
    const query = `Analyse les performances globales.`;
    const response = await callMistralAI(query);
    setAiAnalysis(response);
    setSelectedProduct(null);
    setShowAiModal(true);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0 FC';
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FC';
  };

  const formatDateCache = (dateString) => {
    if (!dateString) return 'Jamais';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date invalide';
    return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
  };

  // Filtrage
  const [filteredData, setFilteredData] = useState([]);
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      if (!searchTerm) {
        setFilteredData(data);
      } else {
        const s = searchTerm.toLowerCase();
        setFilteredData(data.filter(item =>
          item.description?.toLowerCase().includes(s) ||
          item.company_name?.toLowerCase().includes(s)
        ));
      }
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [data, searchTerm]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleExport = () => {
    const headers = ['ID', 'Entreprise', 'Description', 'Prix unitaire', 'Total', 'Adresse', 'Date'];
    const csvData = data.map(a => [a.id, a.company_name, a.description, a.unit_price, a.total, a.address || '', a.created_at || '']);
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ventes_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    setSuccessMessage('Export CSV effectué');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  if (loading && !data.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-blue-950/50 to-indigo-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-400/20 border-t-blue-400 rounded-full animate-spin" />
          <p className="text-slate-300">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950/50 to-indigo-950 relative">
      {/* Fond décoratif */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative max-w-7xl mx-auto p-3 md:p-6 pb-24 md:pb-6">
        {/* Barre d'info cache */}
        <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-3 md:p-4 mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs md:text-sm text-slate-300">
          <div className="flex items-center gap-2 flex-wrap">
            <FiDatabase className="text-blue-400" />
            <span>{cacheDate ? `📦 Cache du ${formatDateCache(cacheDate)}` : '🔄 Données fraîches'}</span>
            <FiClock className="text-amber-400 ml-2" />
            <span>Cache 24h</span>
          </div>
          <button onClick={clearCacheAndReload} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-xl text-xs">Vider cache</button>
        </div>

        {/* En-tête desktop */}
        <div className="hidden md:block mb-6">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent flex items-center gap-2">
                  <FiBarChart2 /> Analyse des Ventes <FiTrendingUp />
                </h1>
                <p className="text-slate-400 text-sm mt-1">Tableau de bord avec IA Mistral</p>
              </div>
              <div className="flex gap-3">
                <button onClick={manualRefresh} disabled={isRefreshing} className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl text-sm disabled:opacity-50">
                  <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} /> {isRefreshing ? 'Mise à jour...' : 'Actualiser'}
                </button>
                <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/10 border border-blue-400/20 text-blue-400 rounded-xl text-sm">
                  <FiDownload /> Exporter
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* En-tête mobile */}
        <div className="md:hidden mb-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-white">Analyse Ventes</h1>
            <p className="text-slate-400 text-xs">{stats.totalArticles} articles</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setMobileMenuOpen(true)} className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-300">
              <FiMenu size={18} />
            </button>
          </div>
        </div>

        {/* Stats de base */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
          {[
            { label: 'Articles', value: stats.totalArticles, icon: FiPackage, color: 'text-blue-400' },
            { label: 'Valeur totale', value: formatCurrency(stats.totalValue), icon: FiDollarSign, color: 'text-emerald-400' },
            { label: 'Prix moyen', value: formatCurrency(stats.averagePrice), icon: FiTrendingUp, color: 'text-purple-400' },
            { label: 'Médiane', value: formatCurrency(stats.medianPrice), icon: FiPercent, color: 'text-cyan-400' },
          ].map((s, i) => (
            <div key={i} className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 md:p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-blue-400/50 to-purple-400/50" />
              <s.icon className={`text-2xl mb-2 ${s.color}`} />
              <div className="text-white font-bold text-lg md:text-xl">{s.value}</div>
              <div className="text-slate-400 text-xs">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Stats de dispersion et concentration */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
          {[
            { label: 'Écart-type', value: formatCurrency(stats.stdDevPrice), icon: FiActivity, color: 'text-amber-400' },
            { label: 'Coeff. variation', value: stats.coefficientVariation.toFixed(1) + '%', icon: FiBarChart2, color: 'text-pink-400' },
            { label: 'Q1 - Q3', value: `${formatCurrency(stats.q1)} - ${formatCurrency(stats.q3)}`, icon: FiTarget, color: 'text-indigo-400' },
            { label: 'Indice HHI', value: stats.hhi.toFixed(0), icon: FiLayers, color: 'text-orange-400' },
          ].map((s, i) => (
            <div key={i} className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 md:p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-blue-400/50 to-purple-400/50" />
              <s.icon className={`text-2xl mb-2 ${s.color}`} />
              <div className="text-white font-bold text-lg md:text-xl">{s.value}</div>
              <div className="text-slate-400 text-xs">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Graphiques Donut & Bar */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-4 md:mb-8">
          <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-5">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiPieChart className="text-violet-400" /> Distribution des prix</h3>
            <DonutChart data={donutData} size={200} />
          </div>
          <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-5">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiBarChart2 className="text-amber-400" /> Top entreprises (CA)</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" stroke="rgba(255,255,255,0.5)" fontSize={10} />
                <YAxis stroke="rgba(255,255,255,0.5)" tickFormatter={(v) => formatCurrency(v)} />
                <Tooltip contentStyle={{ background: 'rgba(15,25,55,0.95)', borderRadius: '16px', border: '1px solid rgba(148,163,184,0.1)', color: 'white' }} formatter={(v) => [formatCurrency(v), 'CA']} />
                <Bar dataKey="value" fill="#10b981" radius={[10,10,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Scatter plot : Positionnement des entreprises */}
        {stats.scatterData.length > 0 && (
          <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-5 mb-4 md:mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiBarChart className="text-teal-400" /> Positionnement des entreprises</h3>
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="articleCount" name="Nb articles" stroke="rgba(255,255,255,0.5)" label={{ value: "Nombre d'articles", position: 'bottom', fill: 'white' }} />
                <YAxis dataKey="avgPrice" name="Prix moyen" stroke="rgba(255,255,255,0.5)" tickFormatter={(v) => formatCurrency(v)} label={{ value: 'Prix moyen', angle: -90, position: 'left', fill: 'white' }} />
                <ZAxis range={[50, 200]} />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={{ background: 'rgba(15,25,55,0.95)', borderRadius: '16px', border: '1px solid rgba(148,163,184,0.1)', color: 'white' }} formatter={(value, name) => name === 'avgPrice' ? formatCurrency(value) : value} />
                <Legend />
                <Scatter name="Entreprises" data={stats.scatterData} fill="#8b5cf6" />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Analyse par entreprise */}
        <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-5 mb-4 md:mb-8">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiUsers className="text-green-400" /> Analyse par entreprise</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-slate-400">
                  <th className="p-3">Entreprise</th>
                  <th className="p-3 text-right">Nb articles</th>
                  <th className="p-3 text-right">Prix moyen</th>
                  <th className="p-3 text-right">Min</th>
                  <th className="p-3 text-right">Max</th>
                  <th className="p-3 text-right">Part valeur</th>
                  <th className="p-3 text-right">Part volume</th>
                </tr>
              </thead>
              <tbody>
                {stats.companyAnalysis.map((c, idx) => (
                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 text-white">{c.name}</td>
                    <td className="p-3 text-right">{c.articleCount}</td>
                    <td className="p-3 text-right text-emerald-400">{formatCurrency(c.avgPrice)}</td>
                    <td className="p-3 text-right">{formatCurrency(c.minPrice)}</td>
                    <td className="p-3 text-right">{formatCurrency(c.maxPrice)}</td>
                    <td className="p-3 text-right text-blue-400">{c.shareValue.toFixed(1)}%</td>
                    <td className="p-3 text-right text-amber-400">{c.shareVolume.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Comparaison des prix par article */}
        <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-5 mb-4 md:mb-8">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiShoppingBag className="text-pink-400" /> Comparaison des prix par article</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-slate-400">
                  <th className="p-3">Description</th>
                  <th className="p-3 text-right">Occurrences</th>
                  <th className="p-3 text-right">Entreprises</th>
                  <th className="p-3 text-right">Min</th>
                  <th className="p-3 text-right">Max</th>
                  <th className="p-3 text-right">Moyenne</th>
                </tr>
              </thead>
              <tbody>
                {stats.productComparison.slice(0, 10).map((p, idx) => (
                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 text-white">{p.description}</td>
                    <td className="p-3 text-right">{p.count}</td>
                    <td className="p-3 text-right">{p.companies}</td>
                    <td className="p-3 text-right text-emerald-400">{formatCurrency(p.min)}</td>
                    <td className="p-3 text-right text-red-400">{formatCurrency(p.max)}</td>
                    <td className="p-3 text-right text-blue-400">{formatCurrency(p.avg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Barres de progression */}
        <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-5 mb-4 md:mb-8">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiActivity className="text-cyan-400" /> Répartition par tranche de prix</h3>
          <div className="space-y-4">
            {stats.priceRanges.map((range, i) => (
              <ProgressBar key={i} label={range.label} value={range.count} total={stats.totalArticles} color={range.color} />
            ))}
          </div>
        </div>

        {/* Top 5 entreprises / Top 5 produits */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-4 md:mb-8">
          <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-5">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiAward className="text-amber-400" /> Top 5 entreprises</h3>
            {stats.topCompanies.map((company, i) => (
              <div key={i} className="flex justify-between items-center py-2.5 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-black' : i === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-black' : i === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' : 'bg-blue-500/20 text-blue-400'}`}>{i + 1}</span>
                  <span className="text-slate-200 text-sm">{company.name}</span>
                </div>
                <span className="text-emerald-400 font-mono text-sm">{formatCurrency(company.value)}</span>
              </div>
            ))}
          </div>
          <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-5">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiShoppingBag className="text-pink-400" /> Top 5 produits chers</h3>
            {stats.topProducts.map((product, i) => (
              <div key={i} className="flex justify-between items-center py-2.5 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-black' : i === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-black' : i === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' : 'bg-blue-500/20 text-blue-400'}`}>{i + 1}</span>
                  <span className="text-slate-200 text-sm truncate max-w-[200px]">{product.description}</span>
                </div>
                <span className="text-emerald-400 font-mono text-sm">{formatCurrency(product.unit_price)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Section IA */}
        <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-purple-400/20 rounded-3xl p-5 mb-4 md:mb-8">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiCpu className="text-purple-400" /> Analyse IA Mistral</h3>
          <div className="flex flex-col md:flex-row gap-3 mb-3">
            <div className="relative flex-1">
              <FiMessageCircle className="absolute left-3 top-3 text-slate-500" />
              <input
                type="text"
                placeholder="Posez une question sur vos ventes..."
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && askAI()}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-purple-400/20 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-400/20"
              />
            </div>
            <button onClick={askAI} disabled={isAiLoading} className="px-6 py-3 bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
              {isAiLoading ? <FiZap className="animate-spin" /> : <FiZap />} {isAiLoading ? 'Analyse...' : 'Interroger IA'}
            </button>
          </div>
          <button onClick={getGeneralAnalysis} className="w-full py-3 bg-white/5 border border-white/10 rounded-xl text-slate-300 text-sm flex items-center justify-center gap-2 hover:bg-white/10">
            <FiTrendingUp /> Analyse générale des performances
          </button>
        </div>

        {/* Barre de recherche et tableau */}
        <div className="hidden md:flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 min-w-[250px]">
            <FiSearch className="absolute left-4 top-3 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher un article..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-blue-400/20 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400/20"
            />
          </div>
        </div>
        <div className="md:hidden mb-4">
          <div className="relative">
            <FiSearch className="absolute left-4 top-3 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-blue-400/20 rounded-2xl text-white placeholder-slate-500"
            />
          </div>
        </div>

        {/* Tableau desktop / Cartes mobiles */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl overflow-hidden"
        >
          {/* Table desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-left text-sm text-slate-400">
                  <th className="p-4">ID</th>
                  <th className="p-4">Entreprise</th>
                  <th className="p-4">Adresse</th>
                  <th className="p-4">Description</th>
                  <th className="p-4 text-right">Prix unitaire</th>
                  <th className="p-4 text-right">Total</th>
                  <th className="p-4">Date</th>
                  <th className="p-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr><td colSpan={8} className="p-8 text-center text-slate-500">Aucun article trouvé</td></tr>
                ) : (
                  paginatedData.map((item) => (
                    <tr key={item.id} className="border-b border-white/5 hover:bg-white/5 transition-colors text-sm">
                      <td className="p-4 font-mono text-blue-400">#{item.id}</td>
                      <td className="p-4 text-slate-300">{item.company_name}</td>
                      <td className="p-4 text-slate-300 max-w-[150px] truncate">{item.address || '—'}</td>
                      <td className="p-4 text-white font-medium">{item.description}</td>
                      <td className="p-4 text-right text-emerald-400 font-mono">{formatCurrency(item.unit_price)}</td>
                      <td className="p-4 text-right text-slate-300 font-mono">{formatCurrency(item.total)}</td>
                      <td className="p-4 text-slate-400">{item.created_at ? new Date(item.created_at).toLocaleDateString('fr-FR') : '—'}</td>
                      <td className="p-4 text-center">
                        <button onClick={() => analyzeProduct(item)} className="px-3 py-1.5 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs hover:bg-purple-500/20">
                          🔍 Analyser
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Cartes mobiles */}
          <div className="md:hidden p-4 space-y-3">
            {paginatedData.length === 0 ? (
              <div className="text-center py-10 text-slate-400">Aucun article</div>
            ) : (
              paginatedData.map((item) => (
                <ArticleCardMobile key={item.id} article={item} onAnalyze={analyzeProduct} formatCurrency={formatCurrency} />
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 border-t border-white/10">
              <div className="text-sm text-slate-400">
                {((currentPage - 1) * itemsPerPage) + 1}–{Math.min(currentPage * itemsPerPage, filteredData.length)} sur {filteredData.length}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronsLeft size={16} /></button>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronLeft size={16} /></button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  return (
                    <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`w-8 h-8 rounded-lg text-sm font-medium ${currentPage === pageNum ? 'bg-blue-500/20 text-blue-400 border border-blue-400/30' : 'text-slate-400 hover:bg-white/10'}`}>{pageNum}</button>
                  );
                })}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronRight size={16} /></button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronsRight size={16} /></button>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <span>Afficher</span>
                <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white">
                  <option value={5}>5</option><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                </select>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Modal IA */}
      <AnimatePresence>
        {showAiModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={() => setShowAiModal(false)}>
            <motion.div initial={{ scale: 0.9, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 50 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border border-purple-400/20 rounded-3xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2"><FiCpu className="text-purple-400" /> Analyse IA</h2>
                <button onClick={() => setShowAiModal(false)} className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white"><FiX size={20} /></button>
              </div>
              {selectedProduct && (
                <div className="mb-4 p-3 bg-blue-500/10 border border-blue-400/20 rounded-xl text-sm">
                  <strong className="text-blue-300">Produit :</strong> {selectedProduct.description}<br />
                  <strong className="text-blue-300">Prix :</strong> {formatCurrency(selectedProduct.unit_price)}
                </div>
              )}
              <div className="p-4 bg-white/5 rounded-xl text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">
                {isAiLoading ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-4 border-purple-400/20 border-t-purple-400 rounded-full animate-spin" />
                    <p>Analyse en cours...</p>
                  </div>
                ) : (
                  aiAnalysis
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Menu mobile */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onRefresh={manualRefresh}
        onClearCache={clearCacheAndReload}
        onExport={handleExport}
        isRefreshing={isRefreshing}
      />

      {/* Notifications */}
      <AnimatePresence>
        {successMessage && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="fixed top-4 right-4 z-50 px-5 py-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-2xl backdrop-blur-xl text-sm">
            {successMessage}
          </motion.div>
        )}
        {error && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="fixed top-4 right-4 z-50 px-5 py-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl backdrop-blur-xl text-sm">
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Statut connexion */}
      <div className={`fixed bottom-4 right-4 z-50 px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 backdrop-blur-xl border ${
        isOnline ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
      }`}>
        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
        <span>{isOnline ? 'En ligne' : 'Hors ligne'}</span>
      </div>
    </div>
  );
};

export default SalesAnalytics;